<template>
  <aside class="right-sidebar">
    <!-- Theme Color Picker (only on theme test page) -->
    <ThemeColorPicker v-if="isThemeTestPage" />

    <!-- Queue Section (Desktop only, at top) -->
    <section v-if="!isThemeTestPage && !isMobile && hasQueue" class="right-sidebar__widget right-sidebar__widget--queue">
      <header class="right-sidebar__widget-header">
        <v-icon size="18" class="right-sidebar__widget-icon">mdi-playlist-play</v-icon>
        <h2 class="right-sidebar__widget-title">Queue</h2>
        <v-chip v-if="upcomingCount > 0" size="x-small" variant="text" class="right-sidebar__queue-count">
          {{ upcomingCount }}
        </v-chip>
      </header>

      <div class="right-sidebar__queue">
        <!-- Current Track -->
        <div
          v-if="currentTrack"
          class="right-sidebar__queue-item right-sidebar__queue-item--current"
          @click="togglePlay"
        >
          <div class="right-sidebar__queue-artwork">
            <v-img
              v-if="currentTrackArtwork"
              :src="currentTrackArtwork"
              cover
            />
            <v-icon v-else size="16">mdi-music</v-icon>
            <div v-if="isPlaying" class="right-sidebar__queue-playing">
              <div class="right-sidebar__queue-equalizer">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
          <div class="right-sidebar__queue-info">
            <span class="right-sidebar__queue-title">{{ currentTrackTitle }}</span>
            <span class="right-sidebar__queue-artist">{{ currentTrackArtist }}</span>
          </div>
          <v-icon
            size="14"
            class="right-sidebar__queue-play"
            :icon="isPlaying ? 'mdi-pause' : 'mdi-play'"
          />
        </div>

        <!-- Upcoming Tracks (max 3) -->
        <div v-if="upcomingTracks.length > 0" class="right-sidebar__queue-upcoming">
          <div
            v-for="(track, index) in upcomingTracks"
            :key="track.ulid || track.id"
            class="right-sidebar__queue-item"
            @click="playTrack(index)"
          >
            <div class="right-sidebar__queue-artwork">
              <v-img
                v-if="getTrackArtwork(track)"
                :src="getTrackArtwork(track)!"
                cover
              />
              <v-icon v-else size="14">mdi-music</v-icon>
            </div>
            <div class="right-sidebar__queue-info">
              <span class="right-sidebar__queue-title">{{ getTrackTitle(track) }}</span>
              <span class="right-sidebar__queue-artist">{{ getTrackArtist(track) }}</span>
            </div>
            <v-icon size="14" class="right-sidebar__queue-play">mdi-play</v-icon>
          </div>
        </div>

        <!-- View All Link -->
        <button
          v-if="queue.length > 4"
          class="right-sidebar__queue-view-all"
          @click="openQueuePanel"
        >
          View all {{ queue.length }} tracks
          <v-icon size="14">mdi-chevron-right</v-icon>
        </button>
      </div>
    </section>

    <!-- Trending Section -->
    <TrendingNowWidget v-if="!isThemeTestPage" :show-explore-more="isMobile" class="right-sidebar__trending-widget" />

    <!-- Invite Friends Modal -->
    <InviteFriendsModal v-model="showInviteModal" @invited="handleInvited" />

    <!-- Action Feedback Overlay -->
    <ActionFeedbackOverlay
      :show="feedback.show"
      :icon="feedback.icon"
      :text="feedback.text"
      :type="feedback.type"
    />
  </aside>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { useAuth } from "@/composables/useAuth";
import { useSdk } from "@/composables/useSdk";
import { useAudioPlayer } from "@/composables/useAudioPlayer";
import { useActionFeedback } from "@/composables/useActionFeedback";
import ActionFeedbackOverlay from "@/components/feedback/ActionFeedbackOverlay.vue";
import InviteFriendsModal from "@/components/modals/InviteFriendsModal.vue";
import ThemeColorPicker from "@/components/theme/ThemeColorPicker.vue";
import TrendingNowWidget from "@/components/layout/TrendingNowWidget.vue";
import { formatBodyAsPlainText } from "@/utils/postUtils";
import { emitFollowChanged } from "@/utils/followEvents";
import type { FeedFilter, Post } from "@/types";
import type { TrendingUser, SuggestedUser as SdkSuggestedUser } from "@cc-consulting-nv/ccsdk";

const router = useRouter();
const { isAuthenticated } = useAuth();
const { sdk, isInitialized } = useSdk();
const { mobile: isMobile } = useDisplay();
const {
  queue,
  currentTrack,
  queueIndex,
  isPlaying,
  playAtIndex,
  togglePlay,
  openQueuePanel,
} = useAudioPlayer();
const { feedback, showFeedback } = useActionFeedback();

// Check if on theme test page
const isThemeTestPage = computed(() => router.currentRoute.value.path === "/test/theme");

// Show filter content (not on theme test page)
const showFilterContent = computed(() => !isThemeTestPage.value);

// Queue computed properties
const hasQueue = computed(() => queue.value.length > 0);
const upcomingTracks = computed(() => {
  if (queueIndex.value < 0) return queue.value.slice(0, 3);
  return queue.value.slice(queueIndex.value + 1, queueIndex.value + 4);
});
const upcomingCount = computed(() => {
  if (queueIndex.value < 0) return queue.value.length;
  return Math.max(0, queue.value.length - queueIndex.value - 1);
});

const currentTrackArtwork = computed(() => {
  if (!currentTrack.value) return null;
  const images = currentTrack.value.images as Array<{ url?: string }> | undefined;
  return images?.[0]?.url || null;
});

const currentTrackTitle = computed(() => {
  if (!currentTrack.value) return "";
  const title = typeof currentTrack.value.title === "string" ? currentTrack.value.title : null;
  const body = typeof currentTrack.value.body === "string" ? currentTrack.value.body : null;
  return title || (body ? formatBodyAsPlainText(body) : null) || "Unknown Track";
});

const currentTrackArtist = computed(() => {
  if (!currentTrack.value) return "";
  if (currentTrack.value.artist) return currentTrack.value.artist;
  const user = (currentTrack.value as any).user;
  return user?.name || user?.displayName || user?.username || "Unknown Artist";
});

// Queue helper functions
function getTrackArtwork(track: Post): string | null {
  const images = track.images as Array<{ url?: string }> | undefined;
  return images?.[0]?.url || null;
}

function getTrackTitle(track: Post): string {
  const title = typeof track.title === "string" ? track.title : null;
  const body = typeof track.body === "string" ? track.body : null;
  return title || (body ? formatBodyAsPlainText(body) : null) || "Unknown Track";
}

function getTrackArtist(track: Post): string {
  if (track.artist) return track.artist;
  const user = (track as any).user;
  return user?.name || user?.displayName || user?.username || "Unknown Artist";
}

function playTrack(index: number) {
  const actualIndex = queueIndex.value + 1 + index;
  if (actualIndex >= 0 && actualIndex < queue.value.length) {
    playAtIndex(actualIndex);
    // Start playback if not already playing
    if (!isPlaying.value) {
      togglePlay();
    }
  }
}

// Modal state
const showInviteModal = ref(false);

const selectedFilter = ref<FeedFilter>("all");

interface ContentFilter {
  value: FeedFilter;
  label: string;
  icon: string;
}

const contentFilters: ContentFilter[] = [
  { value: "all", label: "All", icon: "mdi-view-grid-outline" },
  { value: "songs", label: "Songs", icon: "mdi-music" },
  { value: "videos", label: "Videos", icon: "mdi-video-outline" },
  { value: "podcasts", label: "Podcasts", icon: "mdi-podcast" },
  { value: "bursts", label: "Bursts", icon: "mdi-lightning-bolt" },
];

// Suggested users with follow state (extends SDK type with UI state)
interface SuggestedUser extends Omit<SdkSuggestedUser, 'avatar' | 'ProfileEngagement'> {
  avatar: string;
  ProfileEngagement?: SdkSuggestedUser['ProfileEngagement'];
  isFollowing: boolean;
  isFollowLoading: boolean;
  isHovered?: boolean;
}

import { followButton } from "@cc-consulting-nv/ccsdk";

function getFollowBtn(user: SuggestedUser) {
  return followButton({
    isFollowing: user.isFollowing,
    isFollowingYou: user.ProfileEngagement?.isFollowingYou ?? false,
  });
}

function getFollowButtonClass(user: SuggestedUser): Record<string, boolean> {
  const { state } = getFollowBtn(user);
  const isHovered = user.isHovered ?? false;
  const isFollowingLike = state === 'following' || state === 'mutual';
  return {
    'right-sidebar__follow-btn--default': state === 'not-following',
    'right-sidebar__follow-btn--follows-you': state === 'follows-you',
    'right-sidebar__follow-btn--following': isFollowingLike,
    'right-sidebar__follow-btn--mutual': state === 'mutual',
    'right-sidebar__follow-btn--hover-unfollow': isFollowingLike && isHovered,
  };
}

function getFollowIcon(user: SuggestedUser): string {
  const { state, icon } = getFollowBtn(user);
  const isHovered = user.isHovered ?? false;
  if ((state === 'following' || state === 'mutual') && isHovered) {
    return 'mdi-account-minus';
  }
  return icon;
}

function getFollowButtonText(user: SuggestedUser): string {
  const { state, text } = getFollowBtn(user);
  const isHovered = user.isHovered ?? false;
  if ((state === 'following' || state === 'mutual') && isHovered) {
    return 'Unfollow';
  }
  return text;
}

function getFollowAriaLabel(user: SuggestedUser): string {
  const { state } = getFollowBtn(user);
  const name = user.name || user.username;
  switch (state) {
    case 'mutual':
      return `Unfollow ${name} (friend)`;
    case 'following':
      return `Unfollow ${name}`;
    case 'follows-you':
      return `Follow back ${name} who follows you`;
    default:
      return `Follow ${name}`;
  }
}

const suggestedUsers = ref<SuggestedUser[]>([]);
const isLoadingSuggestions = ref(false);

const emit = defineEmits<{
  "filter-change": [filter: FeedFilter];
  "open-settings": [];
}>();

function selectFilter(filter: FeedFilter) {
  selectedFilter.value = filter;
  emit("filter-change", filter);
}

watch(selectedFilter, (newFilter) => {
  emit("filter-change", newFilter);
});

// Load suggested users
async function loadSuggestedUsers() {
  if (!isAuthenticated.value || !isInitialized.value || !sdk) return;

  isLoadingSuggestions.value = true;
  try {
    // Use dedicated suggestions endpoint - returns personalized suggestions
    // based on friends-of-friends and popular users
    // SDK unwraps the response, so we get SuggestedUser[] directly with ProfileEngagement
    const users = await sdk.getUserSuggestions(5);

    suggestedUsers.value = (users || [])
      .filter((u) => !!u.ulid && !!u.username)
      .map((u): SuggestedUser => ({
        ulid: u.ulid,
        name: u.name,
        username: u.username,
        avatar: u.avatar || "",
        bio: u.bio,
        followerCount: u.followerCount || 0,
        ProfileEngagement: u.ProfileEngagement,
        // Use ProfileEngagement.isFollowing if available, otherwise default to false
        isFollowing: u.ProfileEngagement?.isFollowing ?? false,
        isFollowLoading: false,
      }));
  } catch (err) {
    console.error("Failed to load suggested users:", err);
    suggestedUsers.value = [];
  } finally {
    isLoadingSuggestions.value = false;
  }
}

// Toggle follow for a suggested user
async function toggleFollow(user: SuggestedUser) {
  if (user.isFollowLoading) return;

  user.isFollowLoading = true;
  try {
    if (user.isFollowing) {
      if (user.username) {
        await sdk.unfollowUser(user.username);
      } else {
        await sdk.unfollowUserByUlid(user.ulid);
      }
      user.isFollowing = false;
      emitFollowChanged("unfollow", { ulid: user.ulid, username: user.username });
    } else {
      if (user.username) {
        await sdk.followUser(user.username);
      } else {
        await sdk.followUserByUlid(user.ulid);
      }
      user.isFollowing = true;
      emitFollowChanged("follow", { ulid: user.ulid, username: user.username });
    }
  } catch (err) {
    console.error("Failed to toggle follow:", err);
  } finally {
    user.isFollowLoading = false;
  }
}

// Navigate to user profile
function navigateToProfile(user: SuggestedUser) {
  router.push(`/profile/${user.username}`);
}

// Handle successful invitation
function handleInvited(emails: string | string[]) {
  console.log("Successfully invited:", emails);
  // Optionally refresh suggested users after inviting
  if (isAuthenticated.value) {
    loadSuggestedUsers();
  }
}

// Load data on mount
onMounted(() => {
  if (isAuthenticated.value) {
    loadSuggestedUsers();
  }
});

// Reload when authentication changes
watch(isAuthenticated, (newVal) => {
  if (newVal) {
    loadSuggestedUsers();
  } else {
    suggestedUsers.value = [];
  }
});
</script>

<style scoped lang="scss">
.right-sidebar {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  max-height: 90vh;
  overflow: hidden;

  // Widget
  &__widget {
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 20px;
    overflow-y: hidden;
    flex-shrink: 1;
    display: flex;
    flex-direction: column;
  }

  &__widget-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }

  &__widget-icon {
    color: rgb(var(--v-theme-primary));
  }

  &__widget-title {
    font-family: var(--font-display);
    font-size: 1rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    margin: 0;
  }

  &__queue-count {
    margin-left: auto;
    font-size: 0.6875rem;
  }

  // Queue
  &__queue {
    padding: 8px;
  }

  &__queue-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.04);

      .right-sidebar__queue-play {
        opacity: 1;
      }
    }

    &--current {
      background: rgba(var(--v-theme-primary), 0.08);
      cursor: default;

      &:hover {
        background: rgba(var(--v-theme-primary), 0.12);
      }
    }
  }

  &__queue-artwork {
    position: relative;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    overflow: hidden;
    background: rgba(var(--v-theme-primary), 0.1);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    .v-icon {
      color: rgb(var(--v-theme-primary));
    }
  }

  &__queue-playing {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.4);
  }

  &__queue-equalizer {
    display: flex;
    align-items: center;
    gap: 2px;
    height: 12px;

    span {
      width: 2px;
      background: rgb(var(--v-theme-primary));
      border-radius: 1px;
      animation: queue-equalizer 1.2s ease-in-out infinite;

      &:nth-child(1) {
        height: 6px;
        animation-delay: 0s;
      }

      &:nth-child(2) {
        height: 10px;
        animation-delay: 0.2s;
      }

      &:nth-child(3) {
        height: 8px;
        animation-delay: 0.4s;
      }
    }
  }

  &__queue-info {
    flex: 1;
    min-width: 0;
  }

  &__queue-title {
    display: block;
    font-weight: 600;
    font-size: 0.8125rem;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__queue-artist {
    display: block;
    font-size: 0.75rem;
    color: rgb(var(--v-theme-outline));
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__queue-play {
    color: rgb(var(--v-theme-outline));
    opacity: 0;
    transition: opacity 0.2s ease;
    flex-shrink: 0;
  }

  &__queue-upcoming {
    margin-top: 4px;
  }

  &__queue-view-all {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 100%;
    padding: 10px;
    margin-top: 8px;
    border: none;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    background: none;
    font-family: var(--font-body);
    font-size: 0.8125rem;
    font-weight: 500;
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-primary), 0.05);
    }
  }

  // Trending
  &__trends {
    padding: 8px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }

  &__trends-loading,
  &__trends-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;

    p {
      font-size: 0.8125rem;
      color: rgb(var(--v-theme-outline));
      margin: 0;
    }
  }

  &__trend-section {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__trend-label {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px 4px;
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgb(var(--v-theme-outline));
  }

  &__trend {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 10px 12px;
    border: none;
    background: none;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;

    &:hover {
      background: rgba(255, 255, 255, 0.04);

      .right-sidebar__trend-arrow {
        opacity: 1;
        transform: translateX(2px);
      }
    }

    &--song,
    &--creator {
      padding: 8px 12px;
    }
  }

  &__trend-cover {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    overflow: hidden;
    background: rgba(var(--v-theme-primary), 0.1);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    .v-icon {
      color: rgb(var(--v-theme-primary));
    }
  }

  &__trend-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;
    background: linear-gradient(
      135deg,
      rgb(var(--v-theme-primary)),
      rgb(var(--v-theme-secondary))
    );
    padding: 2px;
    flex-shrink: 0;

    .v-img,
    span {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: rgb(var(--v-theme-surface-variant));
      font-size: 0.75rem;
      font-weight: 600;
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__trend-content {
    flex: 1;
    min-width: 0;
  }

  &__trend-tag {
    display: block;
    font-weight: 600;
    font-size: 0.875rem;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__trend-count {
    display: block;
    font-size: 0.75rem;
    color: rgb(var(--v-theme-outline));
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__trend-arrow {
    color: rgb(var(--v-theme-outline));
    opacity: 0;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }

  // Filters
  &__filters {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 16px;
  }

  &__filter {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.02);
    border-radius: 50px;
    font-family: var(--font-body);
    font-size: 0.8125rem;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface-variant));
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.06);
      border-color: rgba(255, 255, 255, 0.15);
      color: rgb(var(--v-theme-on-surface));
    }

    &--active {
      background: linear-gradient(
        135deg,
        rgba(var(--v-theme-primary), 0.15),
        rgba(var(--v-theme-primary), 0.05)
      );
      border-color: rgba(var(--v-theme-primary), 0.3);
      color: rgb(var(--v-theme-primary));

      &:hover {
        background: linear-gradient(
          135deg,
          rgba(var(--v-theme-primary), 0.2),
          rgba(var(--v-theme-primary), 0.1)
        );
      }
    }
  }

  // Suggestions
  &__suggestions {
    padding: 8px;
  }

  &__suggestions-loading,
  &__suggestions-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;

    p {
      font-size: 0.8125rem;
      color: rgb(var(--v-theme-outline));
      margin: 0;
    }
  }

  &__suggestion {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 12px;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.03);
    }
  }

  &__suggestion-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    overflow: hidden;
    background: linear-gradient(
      135deg,
      rgb(var(--v-theme-primary)),
      rgb(var(--v-theme-secondary))
    );
    padding: 2px;
    flex-shrink: 0;

    .v-img,
    span {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: rgb(var(--v-theme-surface-variant));
      font-size: 0.9375rem;
      font-weight: 600;
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__suggestion-info {
    flex: 1;
    min-width: 0;
  }

  &__suggestion-name {
    display: block;
    font-weight: 600;
    font-size: 0.9375rem;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__suggestion-handle {
    display: block;
    font-size: 0.8125rem;
    color: rgb(var(--v-theme-outline));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__follow-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-width: 80px;
    padding: 8px 16px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.05);
    border-radius: 50px;
    font-family: var(--font-body);
    font-size: 0.8125rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
      background: rgb(var(--v-theme-on-surface));
      border-color: rgb(var(--v-theme-on-surface));
      color: rgb(var(--v-theme-surface));
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    // Default state - "Follow" button
    &--default {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.2);
      color: rgb(var(--v-theme-on-surface));

      &:hover {
        background: rgb(var(--v-theme-primary));
        border-color: rgb(var(--v-theme-primary));
        color: rgb(var(--v-theme-on-primary));
      }
    }

    // "Follows You" state - highlighted to encourage follow back
    &--follows-you {
      background: rgba(var(--v-theme-primary), 0.12);
      border-color: rgba(var(--v-theme-primary), 0.4);
      color: rgb(var(--v-theme-primary));

      &:hover {
        background: rgb(var(--v-theme-primary));
        border-color: rgb(var(--v-theme-primary));
        color: rgb(var(--v-theme-on-primary));
      }
    }

    // "Following" state - subtle confirmation
    &--following {
      background: transparent;
      border-color: rgba(var(--v-theme-primary), 0.5);
      color: rgb(var(--v-theme-primary));
    }

    // "Friends" / mutual state - success-like styling
    &--mutual {
      background: rgba(var(--v-theme-success), 0.1);
      border-color: rgba(var(--v-theme-success), 0.4);
      color: rgb(var(--v-theme-success));
    }

    // Hover unfollow state for following/mutual
    &--hover-unfollow {
      background: rgba(var(--v-theme-error), 0.1);
      border-color: rgb(var(--v-theme-error));
      color: rgb(var(--v-theme-error));
    }
  }

  &__follow-icon {
    flex-shrink: 0;
  }

  // Show more
  &__show-more {
    display: block;
    width: 100%;
    padding: 14px 20px;
    border: none;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    background: none;
    font-family: var(--font-body);
    font-size: 0.875rem;
    font-weight: 500;
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
    transition: background 0.2s ease;
    text-align: left;

    &:hover {
      background: rgba(var(--v-theme-primary), 0.05);
    }
  }

  &__invite-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: calc(100% - 32px);
    margin: 12px 16px 8px;
    padding: 10px 16px;
    border: 1px solid rgba(var(--v-theme-primary), 0.3);
    border-radius: 20px;
    background: rgba(var(--v-theme-primary), 0.08);
    font-family: var(--font-body);
    font-size: 0.875rem;
    font-weight: 600;
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-primary), 0.15);
      border-color: rgba(var(--v-theme-primary), 0.5);
    }

    &:active {
      transform: scale(0.98);
    }
  }

}

// Queue equalizer animation
@keyframes queue-equalizer {
  0%,
  100% {
    transform: scaleY(0.5);
  }
  50% {
    transform: scaleY(1);
  }
}

// Light theme
.v-theme--light {
  .right-sidebar {
    &__widget {
      background: rgba(0, 0, 0, 0.02);
      border-color: rgba(0, 0, 0, 0.06);
    }

    &__widget-header {
      border-bottom-color: rgba(0, 0, 0, 0.06);
    }

    &__trend {
      &:hover {
        background: rgba(0, 0, 0, 0.03);
      }
    }

    &__filter {
      background: rgba(0, 0, 0, 0.02);
      border-color: rgba(0, 0, 0, 0.1);

      &:hover {
        background: rgba(0, 0, 0, 0.04);
        border-color: rgba(0, 0, 0, 0.15);
      }

      &--active {
        background: linear-gradient(
          135deg,
          rgba(var(--v-theme-primary), 0.12),
          rgba(var(--v-theme-primary), 0.04)
        );
        border-color: rgba(var(--v-theme-primary), 0.3);
      }
    }

    &__suggestion {
      &:hover {
        background: rgba(0, 0, 0, 0.02);
      }
    }

    &__follow-btn {
      background: rgba(0, 0, 0, 0.03);
      border-color: rgba(0, 0, 0, 0.15);

      &:hover {
        background: rgb(var(--v-theme-on-surface));
        border-color: rgb(var(--v-theme-on-surface));
        color: rgb(var(--v-theme-surface));
      }

      &--default {
        background: rgba(0, 0, 0, 0.03);
        border-color: rgba(0, 0, 0, 0.15);
      }

      &--follows-you {
        background: rgba(var(--v-theme-primary), 0.08);
        border-color: rgba(var(--v-theme-primary), 0.3);
      }

      &--mutual {
        background: rgba(var(--v-theme-success), 0.08);
        border-color: rgba(var(--v-theme-success), 0.3);
      }
    }

    &__show-more {
      border-top-color: rgba(0, 0, 0, 0.06);

      &:hover {
        background: rgba(var(--v-theme-primary), 0.05);
      }
    }
  }
}
</style>
